import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  Heading,
  LoadingState,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  StatTile,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { filterRows } from '@/lib/facet-table';
import { useUrlSearch } from '@/lib/use-list-query-state';
import {
  readTargetedAnalysis,
  type TableCell,
  type TableColumn,
  type TableDataset,
  type TargetedAnalysis,
  type TargetedDevice,
  type TargetedRateRow,
  type WindowsDataset,
} from '@/lib/targeted-analytics';

/** Column order and labels for the device split. Mobile first: it is the focus. */
const DEVICES: readonly { key: TargetedDevice; label: string }[] = [
  { key: 'mobile', label: 'Mobile' },
  { key: 'desktop', label: 'Desktop' },
  { key: 'tablet', label: 'Tablet' },
];

/** Row order and labels for the landing-page buckets. */
const PAGE_LABELS: Readonly<Record<string, string>> = {
  program: 'Program',
  category: 'Category',
  location: 'Location',
  'live/virtual': 'Live / Virtual',
  home: 'Home',
  other: 'Other',
};
const PAGE_ORDER = ['program', 'category', 'location', 'live/virtual', 'home', 'other'] as const;

interface Cell {
  sessions: number;
  leads: number;
}
const emptyCell = (): Cell => ({ sessions: 0, leads: 0 });

function formatDate(iso: string | null): string {
  if (!iso) return 'never';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return 'never';
  return when.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRate(cell: Cell): string {
  if (cell.sessions === 0) return '—';
  const pct = ((cell.leads / cell.sessions) * 100).toFixed(2);
  return `${pct}% (${cell.leads}/${cell.sessions})`;
}

/** True when the query failed because the analysis has not been built yet. */
function isNotBuilt(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 404;
}

/** Sum a set of rows into a device → cell map. */
function tallyByDevice(rows: readonly TargetedRateRow[]): Map<string, Cell> {
  const map = new Map<string, Cell>();
  for (const row of rows) {
    const cell = map.get(row.device) ?? emptyCell();
    cell.sessions += row.sessions;
    cell.leads += row.leads;
    map.set(row.device, cell);
  }
  return map;
}

// ─── The AI "what this means" panel, shared by every dataset shape ──────────

function AiSummary({ data }: { data: TargetedAnalysis }): React.ReactElement {
  return (
    <Panel className="p-5">
      <div className="flex items-center justify-between gap-3">
        <Heading variant="card" className="text-portal-ink">
          What this means
        </Heading>
        <Pill tone="info">AI read</Pill>
      </div>
      {data.summary ? (
        <>
          <div className="mt-3 space-y-2.5 text-portal-small leading-relaxed text-portal-ink">
            {data.summary
              .split(/\n{2,}/)
              .map((para) => para.trim())
              .filter(Boolean)
              .map((para, index) => (
                <p key={index}>{para}</p>
              ))}
          </div>
          <p className="mt-3 text-portal-micro text-portal-muted">
            Generated {formatDate(data.generatedAt)}
            {data.summaryModel ? ` by ${data.summaryModel}` : ''}. Refreshes daily.
          </p>
        </>
      ) : (
        <div className="mt-3">
          <EmptyState message="The AI summary has not been generated yet. It appears after the next daily build." />
        </div>
      )}
    </Panel>
  );
}

// ─── Windows dataset (mobile-conversion): device × page rate tables ─────────

interface WindowTableProps {
  label: string;
  windowKey: string;
  rows: readonly TargetedRateRow[];
}

function WindowTable({ label, windowKey, rows }: WindowTableProps): React.ReactElement {
  const inWindow = rows.filter((row) => row.window === windowKey);
  const byPage = new Map<string, Map<string, Cell>>();
  for (const row of inWindow) {
    const devices = byPage.get(row.page) ?? new Map<string, Cell>();
    const cell = devices.get(row.device) ?? emptyCell();
    cell.sessions += row.sessions;
    cell.leads += row.leads;
    devices.set(row.device, cell);
    byPage.set(row.page, devices);
  }
  const pages = PAGE_ORDER.filter((page) => byPage.has(page));
  const overall = tallyByDevice(inWindow);

  return (
    <section className="space-y-3">
      <Heading variant="section" as="h2">
        {label}
      </Heading>
      <DataTable
        headers={['Page', 'Mobile', 'Desktop', 'Tablet']}
        rightAlignedHeaders={[1, 2, 3]}
        cards={[
          ...pages.map((page) => ({ key: page, title: PAGE_LABELS[page] ?? page, devices: byPage.get(page) })),
          { key: 'all', title: 'All pages', devices: overall },
        ].map(({ key, title, devices }) => ({
          key,
          title,
          fields: DEVICES.map((device) => ({ label: device.label, value: formatRate(devices?.get(device.key) ?? emptyCell()) })),
        }))}
      >
        {pages.map((page) => {
          const devices = byPage.get(page);
          return (
            <tr key={page} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small font-bold text-portal-ink">
                {PAGE_LABELS[page] ?? page}
              </td>
              {DEVICES.map(({ key }) => (
                <td key={key} className="p-3 text-portal-small text-right whitespace-nowrap tabular-nums">
                  {formatRate(devices?.get(key) ?? emptyCell())}
                </td>
              ))}
            </tr>
          );
        })}
        <tr className="border-t border-portal-line align-middle bg-[#f8fafc]">
          <td className="p-3 text-portal-small font-bold text-portal-ink">All pages</td>
          {DEVICES.map(({ key }) => (
            <td key={key} className="p-3 text-portal-small font-bold text-right whitespace-nowrap tabular-nums">
              {formatRate(overall.get(key) ?? emptyCell())}
            </td>
          ))}
        </tr>
      </DataTable>
    </section>
  );
}

function WindowsAnalysis({ data, dataset }: { data: TargetedAnalysis; dataset: WindowsDataset }): React.ReactElement {
  const latest = dataset.windows[dataset.windows.length - 1];
  const headline = useMemo(() => {
    if (!latest) return null;
    const overall = tallyByDevice(dataset.rows.filter((row) => row.window === latest.key));
    const mobile = overall.get('mobile') ?? emptyCell();
    const desktop = overall.get('desktop') ?? emptyCell();
    const totalSessions = [...overall.values()].reduce((sum, cell) => sum + cell.sessions, 0);
    return { mobile, desktop, totalSessions };
  }, [dataset.rows, latest]);

  const rate = (cell: Cell): string =>
    cell.sessions === 0 ? '—' : `${((cell.leads / cell.sessions) * 100).toFixed(2)}%`;

  return (
    <div className="space-y-6">
      <AiSummary data={data} />

      {headline && latest ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
          <StatTile
            label={`Mobile conversion (${latest.label})`}
            value={rate(headline.mobile)}
            hint={`${headline.mobile.leads} of ${headline.mobile.sessions} sessions`}
          />
          <StatTile
            label={`Desktop conversion (${latest.label})`}
            value={rate(headline.desktop)}
            hint={`${headline.desktop.leads} of ${headline.desktop.sessions} sessions`}
          />
          <StatTile
            label="Mobile share of sessions"
            value={
              headline.totalSessions === 0
                ? '—'
                : `${((headline.mobile.sessions / headline.totalSessions) * 100).toFixed(0)}%`
            }
            hint={`${latest.label}`}
          />
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-2">
        {dataset.windows.map((window) => (
          <WindowTable key={window.key} label={window.label} windowKey={window.key} rows={dataset.rows} />
        ))}
      </div>

      <p className="text-portal-micro text-portal-muted">
        Conversion rate is leads divided by sessions. Leads are the GA4 key event
        {' '}
        <code className="rounded bg-slate-100 px-1 py-0.5">new_site_thank_you</code>, which lags 24 to
        48 hours, so treat rates as directional while session counts are reliable.
      </p>
    </div>
  );
}

// ─── Table dataset (ppc-cost-per-lead): headline + a formatted table ────────

function formatCell(value: TableCell, format: TableColumn['format']): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  switch (format) {
    case 'currency':
      return Number.isNaN(n) ? String(value) : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case 'int':
      return Number.isNaN(n) ? String(value) : Math.round(n).toLocaleString('en-US');
    case 'number':
      return Number.isNaN(n) ? String(value) : n.toLocaleString('en-US', { maximumFractionDigits: 1 });
    case 'percent':
      return Number.isNaN(n) ? String(value) : `${(n * 100).toFixed(2)}%`;
    default:
      return String(value);
  }
}

function TableAnalysis({ data, dataset }: { data: TargetedAnalysis; dataset: TableDataset }): React.ReactElement {
  const { search, setSearch } = useUrlSearch();
  const numericIndexes = dataset.columns
    .map((col, index) => ({ col, index }))
    .filter(({ col }) => col.format && col.format !== 'text')
    .map(({ index }) => index);
  // The dataset's shape varies by analysis, so the search matches its text columns.
  const textKeys = dataset.columns.filter((col) => !col.format || col.format === 'text').map((col) => col.key);
  const rows = filterRows(dataset.rows, search, textKeys);

  return (
    <div className="space-y-6">
      <AiSummary data={data} />

      {dataset.headline.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {dataset.headline.map((stat) => (
            <StatTile key={stat.label} label={stat.label} value={stat.value} />
          ))}
        </div>
      ) : null}

      {textKeys.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            label="Search rows"
            placeholder={dataset.columns
              .filter((col) => textKeys.includes(col.key))
              .map((col) => col.label)
              .join(', ')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full max-w-sm"
          />
        </div>
      ) : null}

      {rows.length === 0 && dataset.rows.length > 0 ? (
        <Panel>
          <EmptyState title="No rows match" message="Try a different search." />
        </Panel>
      ) : (
        <DataTable
          headers={dataset.columns.map((col) => col.label)}
          rightAlignedHeaders={numericIndexes}
          // Any shape of table: the first column names the card, the rest are its figures.
          cards={rows.map((row, rowIndex) => {
            const [first, ...rest] = dataset.columns;
            return {
              key: String(rowIndex),
              title: first ? formatCell(row[first.key] ?? null, !first.format || first.format === 'text' ? 'text' : first.format) : '—',
              fields: rest.map((col) => ({ label: col.label, value: formatCell(row[col.key] ?? null, col.format) })),
            };
          })}
        >
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-t border-portal-line align-middle">
              {dataset.columns.map((col, colIndex) => {
                const isText = !col.format || col.format === 'text';
                return (
                  <td
                    key={col.key}
                    className={
                      isText
                        ? 'p-3 text-portal-small font-bold text-portal-ink'
                        : 'p-3 text-portal-small text-right whitespace-nowrap tabular-nums'
                    }
                  >
                    {formatCell(row[col.key] ?? null, colIndex === 0 && isText ? 'text' : col.format)}
                  </td>
                );
              })}
            </tr>
          ))}
        </DataTable>
      )}

      <p className="text-portal-micro text-portal-muted">
        Leads and bookings are the Salesforce-connected Google Ads conversion actions, credited to the
        campaign of the click. Performance Max and Demand Gen attribution is coarser than Search, and
        bookings lag leads by weeks.
      </p>
    </div>
  );
}

function Analysis({ data }: { data: TargetedAnalysis }): React.ReactElement {
  if (data.dataset.kind === 'table') {
    return <TableAnalysis data={data} dataset={data.dataset} />;
  }
  return <WindowsAnalysis data={data} dataset={data.dataset} />;
}

/**
 * A single targeted analysis: its AI summary, headline stats and the dataset
 * rendered by shape (a device×page rate grid, or a generic table). Data and
 * summary are computed and cached daily by the API; this page renders the cache.
 */
export function TargetedAnalysisPage(): React.ReactElement {
  const { slug = '' } = useParams<{ slug: string }>();
  const query = useQuery({
    queryKey: ['targeted-analytics', slug],
    queryFn: ({ signal }) => readTargetedAnalysis(slug, signal),
    retry: (count, error) => !isNotBuilt(error) && count < 2,
  });

  return (
    <div className="space-y-6">
      <PageHeading
        eyebrow="Targeted analytics"
        title={query.data?.title ?? 'Targeted analysis'}
        subtitle={query.data?.description}
        size="compact"
        action={
          query.data ? (
            <Pill tone={query.data.generatedAt ? 'neutral' : 'warning'}>
              {query.data.generatedAt
                ? `Last analyzed ${formatDate(query.data.generatedAt)}`
                : 'Not built yet'}
            </Pill>
          ) : undefined
        }
      />

      {query.isLoading ? (
        <LoadingState label="Loading analysis" />
      ) : query.isError ? (
        isNotBuilt(query.error) ? (
          <Panel>
            <EmptyState
              title="Not generated yet"
              message="This analysis has no cached data. Run the daily build (build-targeted-analytics) to populate it."
            />
          </Panel>
        ) : (
          <ErrorNotice title="Could not load analysis" message={errorMessage(query.error)} />
        )
      ) : query.data ? (
        <Analysis data={query.data} />
      ) : null}
    </div>
  );
}
